import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { planningLevels } from "../../../../interfaces/planning";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { Alert, Button } from "react-bootstrap";
import { getPlanningSession, getCoachPlanningMonitoringList, getCoachPlanningMonitoring } from "../../../../services/planning";
import { MonitoringShovelfulsChart } from "../../../charts/monitoring-shovelfuls-chart";
import { MonitoringLaptimesChart } from "../../../charts/monitoring-laptimes-chart";
import { Col, Row } from 'react-bootstrap';
import { UserContext } from "../../../../App";
import { Feedback } from "../training/feedback";
import moment from "moment";

interface PlanningSessionParams {
    season: string;
    team: string;
    day: string;
    moment: string;
    session: string;
}

interface FeedbackData {
    athlete_id: string;
    athlete_name: string;
    fatigue_level: string;
    observations: string;
}

interface ResultsData {
    duration: string;
    speed_avg: string;
    speed_max: string;
    temperature: string;
    wind_speed: string;
    wind_direction: string;
    observations: string;
}

interface MonitoringEvent {
    id: string;
    event_time: string;
    event_type: string;
    event_value: string | null;
}

interface MonitoringMeta {
    sample_shovelfuls: number;
    observations: string;
}

interface MonitoringAthlete {
    athlete_id: number;
    athlete_name: string;
}

interface SessionData {
    season: string;
    season_name: string;
    team: string;
    team_name: string;
    week: string;
    workload: string;
    day: string;
    day_moment: string;
    day_moment_session: string;
    fields: { field: string; value: string }[];
    feedback: FeedbackData[];
    results: ResultsData;
    monitoring: MonitoringEvent[];
    monitoring_meta: MonitoringMeta | null;
    monitoring_athletes: MonitoringAthlete[];
}

function AthletePlanningSession() {
    const { userProfile } = useContext(UserContext);
    const { season, team, day, moment: dayMoment, session } = useParams<Record<keyof PlanningSessionParams, string>>();
    const [loaded, setLoaded] = useState(false);
    const [sessionData, setSessionData] = useState<SessionData | null>(null);
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [duration, setDuration] = useState('');
    const [totalLaps, setTotalLaps] = useState(0);
    const [averageShovelfulsPerMinute, setAverageShovelfulsPerMinute] = useState(0);
    const [lapTimes, setLapTimes] = useState<number[]>([]);
    const [shovelfulsPerMinute, setShovelfulsPerMinute] = useState<number[]>([]);
    const [shovelfulsTimestamps, setShovelfulsTimestamps] = useState<string[]>([]);
    const [boyas, setBoyas] = useState<MonitoringEvent[]>([]);
    const [athleteFeedback, setAthleteFeedback] = useState<FeedbackData | null>(null);
    const [showFeedback, setShowFeedback] = useState(false);
    const [monitoringList, setMonitoringList] = useState<any[]>([]);
    const [selectedMonitoringId, setSelectedMonitoringId] = useState<number | null>(null);
    const [showMonitoringList, setShowMonitoringList] = useState(true);

    useEffect(() => {
        if (season && team && day && dayMoment && session) {
            Promise.all([
                getPlanningSession(parseInt(season), parseInt(team), day, dayMoment, session),
                getCoachPlanningMonitoringList(parseInt(team), day, dayMoment, session)
            ])
                .then(([sessionResponse, monitoringListResponse]: any[]) => {
                    setSessionData(sessionResponse.data);

                    // Filter feedback for current athlete
                    if (sessionResponse.data.feedback && sessionResponse.data.feedback.length > 0) {
                        const currentFeedback = sessionResponse.data.feedback.find((f: FeedbackData) =>
                            f.athlete_id === userProfile.reference_id.toString()
                        );
                        if (currentFeedback) {
                            setAthleteFeedback(currentFeedback);
                        }
                    }

                    // Guardar lista de monitorizaciones filtradas por el atleta actual
                    const monitoringSessions = monitoringListResponse.data || [];
                    const athleteMonitorings = monitoringSessions.filter((m: any) => {
                        return m.athletes && m.athletes.some((a: any) =>
                            a.athlete_id.toString() === userProfile.reference_id.toString()
                        );
                    });
                    setMonitoringList(athleteMonitorings);

                    // Si hay monitorizaciones, mostrar la lista por defecto
                    if (athleteMonitorings.length > 1) {
                        setShowMonitoringList(true);
                    } else if (athleteMonitorings.length === 1) {
                        // Si solo hay una, mostrarla directamente
                        setShowMonitoringList(false);
                        setSelectedMonitoringId(athleteMonitorings[0].monitoring_id);
                        if (athleteMonitorings[0].events) {
                            processMonitoringData(athleteMonitorings[0].events);
                        }
                    } else if (sessionResponse.data.monitoring && sessionResponse.data.monitoring.length > 0) {
                        // Fallback al formato antiguo
                        processMonitoringData(sessionResponse.data.monitoring);
                        setShowMonitoringList(false);
                    }

                    setLoaded(true);
                })
                .catch(() => {
                    setLoaded(true);
                });
        }
    }, [season, team, day, dayMoment, session, userProfile.reference_id]);

    const handleViewMonitoring = (monitoring_id: number) => {
        // Cargar los eventos completos de esta monitorización
        getCoachPlanningMonitoring(parseInt(team || '0'), day || '', dayMoment || '', session || '', monitoring_id)
            .then(response => {
                const data = response.data;
                if (data && data.events) {
                    setSelectedMonitoringId(monitoring_id);
                    processMonitoringData(data.events);
                    setShowMonitoringList(false);
                }
            })
            .catch(error => {
                console.error('Error loading monitoring details:', error);
            });
    };

    const handleBackToMonitoringList = () => {
        setShowMonitoringList(true);
        setSelectedMonitoringId(null);
    };

    const processMonitoringData = (events: MonitoringEvent[]) => {
        const startEvent = events.find((m: MonitoringEvent) => m.event_type === 'Training start');
        const finishEvent = events.find((m: MonitoringEvent) => m.event_type === 'Training finish');
        const lapEvents = events.filter((m: MonitoringEvent) => m.event_type === 'lap');
        const shovelfulEvents = events.filter((m: MonitoringEvent) => m.event_type === 'Shovelful');

        // Hora de inicio
        if (startEvent) {
            setStart(startEvent.event_time);
        } else {
            setStart('');
        }

        // Hora de fin
        if (finishEvent) {
            setEnd(finishEvent.event_time);
        } else {
            const lastElement = events.at(-1) ?? null;
            if (lastElement != null) {
                setEnd(lastElement.event_time);
            } else {
                setEnd('');
            }
        }

        // Duración total
        if (startEvent && finishEvent) {
            const startMoment = moment(startEvent.event_time);
            const finishMoment = moment(finishEvent.event_time);
            const durationMoment = moment.duration(finishMoment.diff(startMoment));
            const hours = Math.floor(durationMoment.asHours());
            const minutes = durationMoment.minutes();
            const seconds = durationMoment.seconds();
            setDuration(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        } else {
            setDuration('');
        }

        // Total de parciales
        setTotalLaps(lapEvents.length);

        // Calcular tiempos de parciales
        const lapTimesArray: number[] = [];
        if (startEvent && lapEvents.length > 0) {
            let previousTime = moment(startEvent.event_time);

            lapEvents.forEach((lap: MonitoringEvent) => {
                const lapTime = moment(lap.event_time);
                const lapDuration = moment.duration(lapTime.diff(previousTime));
                const lapSeconds = lapDuration.asSeconds();
                lapTimesArray.push(lapSeconds);
                previousTime = lapTime;
            });
        }
        setLapTimes(lapTimesArray);

        // Preparar datos de paladas (event_value ya contiene paladas/min)
        const shovelfulsPerMinuteArray: number[] = [];
        const shovelfulsTimestampsArray: string[] = [];
        let totalStrokesPerMinute = 0;

        shovelfulEvents.forEach((event: MonitoringEvent) => {
            const strokesPerMin = parseFloat(event.event_value || '0');
            if (strokesPerMin > 0) {
                shovelfulsPerMinuteArray.push(strokesPerMin);
                totalStrokesPerMinute += strokesPerMin;

                const eventTime = moment(event.event_time);
                shovelfulsTimestampsArray.push(eventTime.format('HH:mm:ss'));
            }
        });

        setShovelfulsPerMinute(shovelfulsPerMinuteArray);
        setShovelfulsTimestamps(shovelfulsTimestampsArray);

        if (shovelfulEvents.length > 0) {
            setAverageShovelfulsPerMinute(totalStrokesPerMinute / shovelfulEvents.length);
        } else {
            setAverageShovelfulsPerMinute(0);
        }

        // Boyas
        setBoyas(events.filter((mf: MonitoringEvent) => mf.event_type.includes('boya')).map((m: MonitoringEvent) => {
            return m;
        }));
    };

    const getFieldValue = (field: string): string => {
        if (!sessionData || !sessionData.fields) return '';
        const fieldData = sessionData.fields.find((f) => f.field === field);
        return fieldData ? fieldData.value : '';
    }

    const handleEnterFeedback = () => {
        setShowFeedback(true);
    }

    const handleFeedbackOnClose = () => {
        setShowFeedback(false);
        // Reload session data to get updated feedback
        if (season && team && day && dayMoment && session) {
            getPlanningSession(parseInt(season), parseInt(team), day, dayMoment, session)
                .then((response: any) => {
                    setSessionData(response.data);
                    if (response.data.feedback && response.data.feedback.length > 0) {
                        const currentFeedback = response.data.feedback.find((f: FeedbackData) =>
                            f.athlete_id === userProfile.reference_id.toString()
                        );
                        if (currentFeedback) {
                            setAthleteFeedback(currentFeedback);
                        }
                    }
                });
        }
    }

    if (!loaded) {
        return <LoadingSpinner />;
    }

    if (!sessionData) {
        return (
            <div className="planning-session-container">
                <h1>{t("Training session")}</h1>
                <Alert variant="info">
                    {t("No data found for this training session.")}
                </Alert>
            </div>
        );
    }

    return (
        <div className="planning-session-container">
            <div className="inner">
                <h1>{t("Training session")}</h1>
                <Row>
                    <Col xs={ 12 } lg={ 6 }>
                        <div className="content-data content-box">
                            <h2>{t("Session information")}</h2>
                            <div className="content-group mb-2">
                                <div className="content-wrapper">
                                    <div className="content-item">
                                        <span className="label">{t("Season")}</span>: {sessionData.season_name}
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("Team")}</span>: {sessionData.team_name}
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("Week")}</span>: {sessionData.week}
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("Workload")}</span>: {sessionData.workload}%
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("Day")}</span>: {moment(sessionData.day).format('DD/MM/YYYY')}
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("Moment")}</span>: {t(sessionData.day_moment)}
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("Session")}</span>: {sessionData.day_moment_session}
                                    </div>
                                </div>
                            </div>

                            <div className="content-group mb-2">
                                <div className="legend">{t("Training details")}</div>
                                <div className="content-wrapper">
                                    <div className="content-item">
                                        <span className="label">{t("Type")}</span>: {getFieldValue('type')}
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("%")}</span>: {getFieldValue('pct')}
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("Training description")}</span>: {getFieldValue('description')}
                                    </div>
                                </div>
                            </div>

                            <div className="content-group mb-2">
                                <div className="legend">{t("Levels")}</div>
                                <div className="content-wrapper">
                                    {planningLevels.map((level) => {
                                        const value = getFieldValue(level.key);
                                        return value ? (
                                            <div className="content-item" key={level.key}>
                                                <span className="label">{level.name}</span>: {value}
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            </div>

                            <div className="content-group mb-2">
                                <div className="legend">{t("Totals")}</div>
                                <div className="content-wrapper">
                                    <div className="content-item">
                                        <span className="label">{t("KM")}</span>: {getFieldValue('KM')}
                                    </div>
                                    <div className="content-item">
                                        <span className="label">{t("TH")}</span>: {getFieldValue('TH')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs={ 12 } lg={ 6 }>
                        <div className="content-data content-box session-actions">
                            <div className="actions">
                                <Button variant="primary" onClick={ handleEnterFeedback }>
                                    { t("Enter feedback") }
                                </Button>
                            </div>
                        </div>
                        {athleteFeedback && (
                            <div className="content-data content-box feedback">
                                <h2>{t("My feedback")}</h2>
                                <div className="feedback-wrapper">
                                    <div className="content-item">
                                        <span className="label">{t("Fatigue level")}</span>: {athleteFeedback.fatigue_level}
                                    </div>
                                    {athleteFeedback.observations && (
                                        <div className="content-item">
                                            <span className="label">{t("Observations")}</span>: {athleteFeedback.observations}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </Col>
                </Row>

                {sessionData.results && Object.keys(sessionData.results).length > 0 && (
                    <>
                        <div className="content-data content-box summary">
                            <h2>{t("Results")}</h2>
                            <div className="totals">
                                <div className="duration report-kpi">
                                    <div className="value">
                                        { moment.utc(parseFloat(sessionData.results.duration)*3600*1000).format('HH:mm:ss') }
                                    </div>
                                    <div className="label">
                                        { t("Duration") }
                                    </div>
                                </div>
                                <div className="temperature report-kpi">
                                    <div className="value">
                                        { parseFloat(sessionData.results.temperature).toFixed(2) } <span className="units">{ t("ºC") }</span>
                                    </div>
                                    <div className="label">
                                        { t("Temperature") }
                                    </div>
                                </div>
                                <div className="speed report-kpi">
                                    <div className="value">
                                        { parseFloat(sessionData.results.speed_avg).toFixed(2) } <span className="units">{ t("k/h") }</span>
                                    </div>
                                    <div className="label">
                                        { t("Avg speed") }
                                    </div>
                                </div>
                                <div className="wind report-kpi">
                                    <div className="value">
                                        { parseFloat(sessionData.results.wind_speed).toFixed(2) } <span className="units">{ t("k/h") }</span>
                                    </div>
                                    <div className="label">
                                        { t("Wind speed") }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {monitoringList.length > 0 && showMonitoringList && (
                    <div className="content-data content-box monitoring">
                        <h2>{t("Monitoring sessions")}</h2>
                        <div className="monitoring-list" style={{display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem'}}>
                            {monitoringList.map((session: any) => (
                                <div key={session.monitoring_id} className="monitoring-item" style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '1rem',
                                    backgroundColor: 'white',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px'
                                }}>
                                    <div className="monitoring-info" style={{flex: 1}}>
                                        <div className="monitoring-header">
                                            <strong>{t("Monitoring")} #{session.monitoring_id}</strong>
                                        </div>
                                        <div className="monitoring-details" style={{
                                            display: 'flex',
                                            gap: '1rem',
                                            marginTop: '0.5rem',
                                            flexWrap: 'wrap',
                                            fontSize: '0.9rem'
                                        }}>
                                            {session.start_time && <span><i className="bi bi-clock"></i> {moment(session.start_time).format('HH:mm:ss')}</span>}
                                            {session.finish_time && <span> - {moment(session.finish_time).format('HH:mm:ss')}</span>}
                                            {session.start_time && session.finish_time && (() => {
                                                const duration = moment.duration(moment(session.finish_time).diff(moment(session.start_time)));
                                                const hours = Math.floor(duration.asHours());
                                                const minutes = duration.minutes();
                                                const seconds = duration.seconds();
                                                const durationStr = hours > 0
                                                    ? `${hours}h ${minutes}m ${seconds}s`
                                                    : minutes > 0
                                                        ? `${minutes}m ${seconds}s`
                                                        : `${seconds}s`;
                                                return <span><i className="bi bi-hourglass-split"></i> {durationStr}</span>;
                                            })()}
                                            {session.lap_count > 0 && <span>{session.lap_count} {t("laps")}</span>}
                                            {session.avg_shovelfuls !== undefined && session.avg_shovelfuls > 0 && <span><i className="bi bi-activity"></i> {parseFloat(session.avg_shovelfuls).toFixed(1)} {t("strokes/min")}</span>}
                                        </div>
                                    </div>
                                    <div className="monitoring-actions" style={{marginLeft: '1rem'}}>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleViewMonitoring(session.monitoring_id)}
                                        >
                                            {t("View")}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {monitoringList.length > 0 && !showMonitoringList && (
                    <div className="content-data content-box monitoring">
                        <h2>{t("Monitoring")}</h2>

                        {monitoringList.length > 1 && (
                            <div className="mb-3" style={{textAlign: 'center'}}>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={handleBackToMonitoringList}
                                >
                                    <i className="bi bi-arrow-left"></i> {t("Back to monitoring list")}
                                </Button>
                            </div>
                        )}

                        <div className="summary-boxes">
                            <div className="summary-box">
                                <div className="label">{ t("Start time") }</div>
                                <div className="value">{ start ? moment(start).format('HH:mm:ss') : '-' }</div>
                            </div>
                            <div className="summary-box">
                                <div className="label">{ t("Finish time") }</div>
                                <div className="value">{ end ? moment(end).format('HH:mm:ss') : '-' }</div>
                            </div>
                            <div className="summary-box">
                                <div className="label">{ t("Total duration") }</div>
                                <div className="value">{ duration || '-' }</div>
                            </div>
                            <div className="summary-box">
                                <div className="label">{ t("Total laps") }</div>
                                <div className="value">{ totalLaps > 0 ? totalLaps : '-' }</div>
                            </div>
                            <div className="summary-box">
                                <div className="label">{ t("Average strokes/min") }</div>
                                <div className="value">{ averageShovelfulsPerMinute > 0 ? averageShovelfulsPerMinute.toFixed(1) : '-' }</div>
                            </div>
                        </div>

                        { shovelfulsPerMinute.length > 0 &&
                            <div className="moment-details">
                                <MonitoringShovelfulsChart data={shovelfulsPerMinute} timestamps={shovelfulsTimestamps} />
                            </div>
                        }
                        { lapTimes.length > 0 &&
                            <div className="boyas">
                                <MonitoringLaptimesChart data={lapTimes} />
                            </div>
                        }
                        { (() => {
                            const selectedMonitoring = monitoringList.find(m => m.monitoring_id === selectedMonitoringId);
                            return selectedMonitoring?.meta?.observations && (
                                <div className="coach-comments mt-3">
                                    <h4>{ t("Coach comments") }</h4>
                                    <div className="comments-content">
                                        { selectedMonitoring.meta.observations }
                                    </div>
                                </div>
                            );
                        })() }
                    </div>
                )}
            </div>
            { showFeedback && sessionData && (
                <Feedback event={{
                    datetime: sessionData.day,
                    day_moment: sessionData.day_moment,
                    day_moment_session: sessionData.day_moment_session
                }} onClose={ handleFeedbackOnClose } />
            )}
        </div>
    );
}

export default AthletePlanningSession;
